/**
 * Runtime configuration — the mechanism release.yml PROMISES and, until now,
 * nothing implemented.
 *
 * The release pipeline deliberately bakes NO VITE_ values into the image
 * ("NO SECRETS AS BUILD ARGS"), because hml and production run the SAME image,
 * byte-identical, promoted by tag — and one image cannot carry two
 * environments' URLs and keys. The values were supposed to arrive "at deploy
 * time via the runtime config". This file is that mechanism's reading half:
 *
 *   1. nginx's /docker-entrypoint.d/10-runtime-config.sh renders
 *      /usr/share/nginx/html/config.js from the CONTAINER environment at start.
 *   2. index.html loads /config.js BEFORE the module bundle, setting
 *      window.__NS_CONFIG.
 *   3. Every consumer reads through runtimeEnv() below: injected value first,
 *      then the build-time import.meta.env — which is how dev slots (whose
 *      images ARE built with the args, from slot-*.env) keep working unchanged.
 *
 * Without this, every CI-built web image threw "VITE_SUPABASE_URL and
 * VITE_SUPABASE_ANON_KEY must be set at build time" on boot — a white screen
 * in hml and production while dev looked fine (2026-07-31).
 *
 * Empty string counts as ABSENT at both layers: the Dockerfile declares the
 * ARGs with default "", so a CI image's import.meta.env carries "" — treating
 * that as a value would defeat the fallback in the exact case it exists for.
 */
export type RuntimeConfigKey =
  | 'VITE_SUPABASE_URL'
  | 'VITE_SUPABASE_ANON_KEY'
  | 'VITE_BFF_BASE_URL'
  | 'VITE_VAPID_PUBLIC_KEY';

declare global {
  interface Window {
    __NS_CONFIG?: Partial<Record<RuntimeConfigKey, string>> | undefined;
  }
}

export function runtimeEnv(key: RuntimeConfigKey): string | undefined {
  const injected = typeof window !== 'undefined' ? window.__NS_CONFIG?.[key] : undefined;
  if (typeof injected === 'string' && injected !== '') {
    return injected;
  }

  const built = import.meta.env[key] as string | undefined;
  return typeof built === 'string' && built !== '' ? built : undefined;
}
